<template>
  <div class="inline-channel-select">
    <h6 class="channel-title">Selecione como deseja receber seu código:</h6>
    <form v-on:submit="sendNewContact" v-on:submit.prevent id="inlineChannelSelectForm">
      <div class="channel-radios">
        <div class="custom-control custom-radio custom-control-inline">
          <input
            type="radio"
            id="inlineChannelRadioSms"
            value="sms"
            name="InlineEmailOrPhone"
            class="custom-control-input"
            v-model="selectedOption"
            @change="selectRadio(2)"
          />
          <label class="custom-control-label pt-1" for="inlineChannelRadioSms">SMS</label>
        </div>
        <div class="custom-control custom-radio custom-control-inline">
          <input
            type="radio"
            value="email"
            class="custom-control-input"
            id="inlineChannelRadioEmail"
            name="InlineEmailOrPhone"
            v-model="selectedOption"
            @change="selectRadio(1)"
          />
          <label class="custom-control-label pt-1" for="inlineChannelRadioEmail">Email</label>
        </div>
      </div>

      <!-- SMS: digitação manual quando não há contatos cadastrados -->
      <div
        class="form-group mb-3"
        v-if="selectedOption === 'sms' && !hasPhoneOptions && !hasEmailOptions"
      >
        <p class="contact-hint">Informe o número de celular para receber o código:</p>
        <the-mask
          type="tel"
          class="form-control form-control-sm"
          placeholder="Digite seu número de celular"
          id="inlineNewPhone"
          v-model="form.newPhone"
          @input="typeNewPhone"
          mask="(##) # ####-####"
        ></the-mask>
      </div>

      <!-- SMS: lista de telefones -->
      <div class="contact-list-block" v-else-if="selectedOption === 'sms'">
        <p class="contact-hint" v-if="hasPhoneOptions">
          Selecione um telefone na lista abaixo:
        </p>
        <p class="contact-hint contact-hint--empty" v-else>
          Não há telefones disponíveis para envio. Tente selecionar e-mail.
        </p>
        <ul class="contact-list" v-if="hasPhoneOptions" role="listbox" aria-label="Telefones disponíveis">
          <li
            v-for="(phone, index) in form.phoneList"
            :key="'phone-' + index"
            role="option"
            :aria-selected="isPhoneSelected(phone)"
            class="contact-option"
            :class="{ selected: isPhoneSelected(phone) }"
            @click="selectPhone(phone)"
          >
            <span class="contact-option-label">{{ phone.PhoneNumber }}</span>
            <span class="contact-option-check" aria-hidden="true" v-if="isPhoneSelected(phone)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12.5L10 17.5L19 7.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </li>
        </ul>
      </div>

      <!-- Email: digitação manual quando não há contatos cadastrados -->
      <div
        class="form-group mb-3"
        v-if="selectedOption === 'email' && !hasPhoneOptions && !hasEmailOptions"
      >
        <p class="contact-hint">Informe o e-mail para receber o código:</p>
        <input
          type="text"
          class="form-control form-control-sm"
          placeholder="Digite seu e-mail"
          id="inlineNewEmail"
          v-model="form.newEmail"
          @input="typeNewEmail"
        />
      </div>

      <!-- Email: lista de e-mails -->
      <div class="contact-list-block" v-else-if="selectedOption === 'email'">
        <p class="contact-hint" v-if="hasEmailOptions">
          Selecione um e-mail na lista abaixo:
        </p>
        <p class="contact-hint contact-hint--empty" v-else>
          Não há e-mails disponíveis para envio. Tente selecionar SMS.
        </p>
        <ul class="contact-list" v-if="hasEmailOptions" role="listbox" aria-label="E-mails disponíveis">
          <li
            v-for="(email, index) in form.emailList"
            :key="'email-' + index"
            role="option"
            :aria-selected="isEmailSelected(email)"
            class="contact-option"
            :class="{ selected: isEmailSelected(email) }"
            @click="selectEmail(email)"
          >
            <span class="contact-option-label">{{ email.Email }}</span>
            <span class="contact-option-check" aria-hidden="true" v-if="isEmailSelected(email)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12.5L10 17.5L19 7.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </li>
        </ul>
      </div>

      <button
        type="submit"
        :disabled="loading || !canSubmit"
        class="btn btn-primary p-3 w-100"
        style="border-radius: 6px; font-size: 16px; background-color: #0046C0; border-color: #0046C0;"
      >
        Receber Token
      </button>
    </form>
    <div v-if="loading" class="login-card-loading">
      <ProgressLoading v-bind:loading="loading" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { TheMask } from "vue-the-mask";
import ProgressLoading from "./ProgressLoading.vue";
import { EventBus } from "@/utils/EventBus";
import { initializeGlobals } from "@/global";

export default {
  name: "inlineChannelSelect",
  components: {
    ProgressLoading,
    TheMask,
  },
  data: () => ({
    form: {
      emailSelected: null,
      phoneSelected: null,
      emailList: [],
      phoneList: [],
      newEmail: "",
      newPhone: "",
    },
    loading: false,
    phoneNumberFinal: null,
    emailFinal: null,
    selectedOption: "sms",
    userDataStorage: {},
    protocol: null,
    host: null,
    ipUser: null,
  }),
  created() {
    this.ipUser = localStorage.getItem("ipUser");
    this.userDataStorage = JSON.parse(localStorage.getItem("userData") || "{}");
    this.protocol = window.location.protocol;
    this.host = window.location.host;

    this.hydrateContactLists();

    // Prefer SMS se houver telefone; senão e-mail.
    if (this.hasPhoneOptions) {
      this.selectedOption = "sms";
    } else if (this.hasEmailOptions) {
      this.selectedOption = "email";
    }
  },
  computed: {
    ...mapState("auth", ["contacts"]),

    hasPhoneOptions() {
      return this.form.phoneList.length > 0;
    },

    hasEmailOptions() {
      return this.form.emailList.length > 0;
    },

    canSubmit() {
      if (this.selectedOption === "sms") {
        return !!(this.form.phoneSelected || this.form.newPhone);
      }
      return !!(this.form.emailSelected || this.form.newEmail);
    },
  },
  methods: {
    ...mapActions("auth", ["ActionSendPromoCode", "ActionSetCurrentForm", "ActionCreateDebtor", "ActionSetContacts"]),

    normalizeContactList(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === "string" && value.trim()) return [value];
      return [];
    },

    hydrateContactLists() {
      const contacts = this.contacts || {};
      const emails = this.normalizeContactList(contacts.ListEmailsWithMask?.Email);
      const phones = this.normalizeContactList(contacts.ListPhonesWithMask?.PhoneNumber);

      this.form.emailList = emails.map((email) => ({ Email: email }));
      this.form.phoneList = phones.map((phone) => ({ PhoneNumber: phone }));
      this.form.emailSelected = this.form.emailList[0] || null;
      this.form.phoneSelected = this.form.phoneList[0] || null;
    },

    selectRadio(value) {
      if (value === 1) {
        this.form.newPhone = "";
        this.form.phoneSelected = null;
        this.form.emailSelected = this.hasEmailOptions ? this.form.emailList[0] : null;
      } else {
        this.form.newEmail = "";
        this.form.emailSelected = null;
        this.form.phoneSelected = this.hasPhoneOptions ? this.form.phoneList[0] : null;
      }
    },

    selectPhone(phone) {
      this.form.phoneSelected = phone;
      this.form.newPhone = "";
    },

    selectEmail(email) {
      this.form.emailSelected = email;
      this.form.newEmail = "";
    },

    isPhoneSelected(phone) {
      return this.form.phoneSelected?.PhoneNumber === phone.PhoneNumber;
    },

    isEmailSelected(email) {
      return this.form.emailSelected?.Email === email.Email;
    },

    typeNewEmail() {
      this.form.emailSelected = null;
    },

    typeNewPhone() {
      this.form.phoneSelected = null;
    },

    eventClickSendCodeGTM() {
      const pageName = this.$options.name;
      const eventCategory = `portal-de-negociacao:porto:${pageName}`;
      const eventData = {
        event: "select_content",
        ev_category: eventCategory,
        ev_action: "click:button:receber-token",
        ev_label: "",
      };
      window.dataLayer.push({ event: "select_content", ...eventData });
    },

    eventSentCodeSuccessGTM(typeSent) {
      const pageName = this.$options.name;
      const eventCategory = `portal-de-negociacao:porto:${pageName}`;
      const eventData = {
        event: "token",
        ev_category: eventCategory,
        ev_action: `token:${typeSent}:sucesso`,
        ev_label: "enviado",
      };
      window.dataLayer.push({ event: "token", ...eventData });
    },

    eventSentCodeErrorGTM(error) {
      const pageName = this.$options.name;
      const eventCategory = `portal-de-negociacao:porto:${pageName}`;
      const eventData = {
        event: "alert",
        ev_category: eventCategory,
        ev_action: "receber-token:alert",
        ev_label: error?.message,
        alert_code: 401,
        error_service: "",
        alert_service_message: error?.message,
        alert_event: "token",
        alert_type: "erro",
      };
      window.dataLayer.push({ event: "alert", ...eventData });
    },

    async sendNewContact() {
      this.eventClickSendCodeGTM();
      this.emailFinal = null;
      this.phoneNumberFinal = null;
      var typeSent = null;

      if (this.selectedOption === "email") {
        if (!this.form.newEmail && !this.form.emailSelected?.Email) {
          this.flashMessage.show({
            timeout: 300,
            status: "error",
            position: "top right",
            blockClass: "custom-block-class",
            title: "Erro",
            message: "informe ao menos um meio de contato",
          });
          return;
        }
        this.emailFinal = this.form.emailSelected ? this.form.emailSelected.Email : this.form.newEmail;
        typeSent = "email";
      }

      if (this.selectedOption === "sms") {
        if (!this.form.newPhone && !this.form.phoneSelected?.PhoneNumber) {
          this.flashMessage.show({
            timeout: 300,
            status: "error",
            position: "top right",
            blockClass: "custom-block-class",
            title: "Erro",
            message: "informe ao menos um meio de contato",
          });
          return;
        }
        this.phoneNumberFinal = this.form.phoneSelected ? this.form.phoneSelected.PhoneNumber : this.form.newPhone;
        typeSent = "sms";
      }

      localStorage.setItem("typesent", typeSent);
      if (this.phoneNumberFinal) {
        localStorage.setItem("lastPhoneNumber", this.phoneNumberFinal);
      } else {
        localStorage.removeItem("lastPhoneNumber");
      }
      if (this.emailFinal) {
        localStorage.setItem("lastEmail", this.emailFinal);
      } else {
        localStorage.removeItem("lastEmail");
      }
      localStorage.removeItem("promoCodeExpiresAt");
      this.loading = true;

      if (this.contacts.SourceInfo === "API_EXTERNA") {
        try {
          var response = await this.ActionCreateDebtor({
            FullName: this.contacts.FullName ? this.contacts.FullName : null,
            DocNumber: this.contacts.DocNumber,
            Email: this.emailFinal,
            PhoneNumber: this.phoneNumberFinal != null ? this.phoneNumberFinal : null,
            OS: navigator.platform,
            IP: this.ipUser ? this.ipUser : "",
          });

          if (response.status === 200) {
            this.ActionSetContacts({
              ...(this.contacts || {}),
              DebtorID: response.data.results.DebtorID,
            });
            this.flashMessage.show({
              timeout: 300,
              status: "success",
              position: "top right",
              blockClass: "custom-block-class",
              title: "Sucesso",
              message: "código enviado com sucesso",
            });
            this.eventSentCodeSuccessGTM(typeSent);
            this.buildAndSetUserData(this.userDataStorage.cpfCnpj, this.userDataStorage.id, this.protocol + "//" + this.host + "/dashboard/codigo-enviado", "codigo-enviado");
            this.emitUpdatePageViewEvent("/dashboard/codigo-enviado", "Código Enviado");
            this.updateCustomData();
            this.ActionSetCurrentForm("form-code-sent");
          } else {
            const error = { message: "ocorreu um erro ao tentar enviar o código de acesso, tente novamente mais tarde" };
            this.flashMessage.show({
              timeout: 300,
              status: "error",
              position: "top right",
              blockClass: "custom-block-class",
              title: "Erro",
              message: error.message,
            });
            this.eventSentCodeErrorGTM(error);
          }
        } catch (error) {
          this.flashMessage.show({
            timeout: 300,
            status: "error",
            position: "top right",
            blockClass: "custom-block-class",
            title: "Erro",
            message: error?.data?.message ?? "Erro na solicitação",
          });
          this.eventSentCodeErrorGTM(error);
        }
      } else {
        var result = await this.ActionSendPromoCode({
          DocNumber: this.contacts.DocNumber,
          DebtorId: this.contacts.DebtorID,
          Email: this.emailFinal,
          PhoneNumber: this.phoneNumberFinal != null ? this.phoneNumberFinal : null,
          OS: navigator.platform,
          IP: this.ipUser ? this.ipUser : "",
        });

        if (result.status === 500) {
          this.flashMessage.show({
            timeout: 300,
            status: "error",
            position: "top right",
            blockClass: "custom-block-class",
            title: "Erro",
            message: result.data?.message ?? "Erro na solicitação",
          });
          this.eventSentCodeErrorGTM(result.data);
        } else {
          if (result.data?.results?.promoCodeExpiresAt) {
            localStorage.setItem("promoCodeExpiresAt", result.data.results.promoCodeExpiresAt);
          }
          this.flashMessage.show({
            timeout: 300,
            status: "success",
            position: "top right",
            blockClass: "custom-block-class",
            title: "Sucesso",
            message: "código enviado com sucesso",
          });
          this.eventSentCodeSuccessGTM(typeSent);
          this.buildAndSetUserData(this.userDataStorage.cpfCnpj, this.userDataStorage.id, this.protocol + "//" + this.host + "/dashboard/codigo-enviado", "codigo-enviado");
          this.emitUpdatePageViewEvent("/dashboard/codigo-enviado", "Código Enviado");
          this.updateCustomData();
          this.ActionSetCurrentForm("form-code-sent");
        }
      }

      this.loading = false;
    },

    buildAndSetUserData(doc, id, pageLocation, pageName) {
      const userData = { cpfCnpj: doc, id: id, page_location: pageLocation, pageName: pageName };
      localStorage.setItem("userData", JSON.stringify(userData));
    },

    emitUpdatePageViewEvent(pageName, friendlyPageName) {
      EventBus.$emit("updatePageView", pageName, friendlyPageName);
    },

    updateCustomData() {
      const globals = initializeGlobals();
      window.customData = globals.customData;
    },
  },
};
</script>

<style scoped>
.channel-title {
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: left;
  color: #1a1a1a;
}

.channel-radios {
  display: flex;
  gap: 1.25rem;
  margin-bottom: 1rem;
  padding-left: 0;
}

.contact-list-block {
  margin-bottom: 1rem;
}

.contact-hint {
  margin: 0 0 0.6rem;
  font-size: 0.875rem;
  color: #6c757d;
  text-align: left;
}

.contact-hint--empty {
  color: #a05a00;
  background: #fff8e6;
  border: 1px solid #f0d9a8;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
}

.contact-list {
  list-style: none;
  margin: 0 0 0.5rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.9rem;
  border: 1.5px solid #dee2e6;
  border-radius: 10px;
  background: #fff;
  color: #212529;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  user-select: none;
}

.contact-option:hover {
  border-color: #0046C0;
  background: rgba(0, 70, 192, 0.03);
}

.contact-option.selected {
  border-color: #0046C0;
  background: rgba(0, 70, 192, 0.08);
  box-shadow: 0 0 0 2px rgba(0, 70, 192, 0.12);
  color: #0046C0;
  font-weight: 600;
}

.contact-option-label {
  font-size: 0.95rem;
  word-break: break-all;
  text-align: left;
}

.contact-option-check {
  flex-shrink: 0;
  display: inline-flex;
  color: #0046C0;
}
</style>
